import React, { useState, useEffect, useCallback } from 'react';

interface HealthStatus {
  status: string;
  timestamp: string;
  version: string;
  environment: string;
}

interface ServiceNowConfig {
  instance: string;
  username: string;
  password: string;
  token: string;
  authType: 'basic' | 'bearer';
}

const ApiTestComponent: React.FC = () => {
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [configTest, setConfigTest] = useState<any>(null);

  const API_BASE_URL = 
    (window as any).ENV_CONFIG?.API_BASE_URL 
      ? `${(window as any).ENV_CONFIG.API_BASE_URL}/api`
      : 'http://localhost:5000/api';

  // Test Health endpoint
  const testHealthEndpoint = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/health`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setHealthStatus(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [API_BASE_URL]);

  // Test ServiceNow endpoint
  const testServiceNowEndpoint = async () => {
    setIsLoading(true);
    setError(null);

    const testConfig: ServiceNowConfig = {
      instance: 'test-instance',
      username: 'test-user',
       password: '',
       token: '',
      authType: 'basic'
    };

    try {
      const response = await fetch(`${API_BASE_URL}/servicenow/validate-connection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testConfig)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setConfigTest(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Test on component mount
  useEffect(() => {
    testHealthEndpoint();
  }, [testHealthEndpoint]);

  return (
    <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
      <h2>🔌 .NET API Connection Test</h2>
      
      <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
        <h3>Health Check</h3>
        <button onClick={testHealthEndpoint} disabled={isLoading}>
          {isLoading ? 'Testing...' : 'Test Health Endpoint'}
        </button>
        
        {healthStatus && (
          <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#d4edda', borderRadius: '3px' }}>
            <strong>✅ API is healthy!</strong>
            <ul style={{ margin: '5px 0' }}>
              <li>Status: {healthStatus.status}</li>
              <li>Version: {healthStatus.version}</li>
              <li>Environment: {healthStatus.environment}</li>
              <li>Timestamp: {new Date(healthStatus.timestamp).toLocaleString()}</li>
            </ul>
          </div>
        )}
      </div>

      <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
        <h3>ServiceNow Validation Test</h3>
        <button onClick={testServiceNowEndpoint} disabled={isLoading}>
          {isLoading ? 'Testing...' : 'Test ServiceNow Endpoint'}
        </button>
        
        {configTest && (
          <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#d4edda', borderRadius: '3px' }}>
            <strong>✅ ServiceNow endpoint working!</strong>
            <ul style={{ margin: '5px 0' }}>
              <li>Success: {configTest.success ? 'Yes' : 'No'}</li>
              <li>Message: {configTest.message}</li>
              <li>Instance: {configTest.instance}</li>
              <li>Auth Type: {configTest.authType}</li>
            </ul>
          </div>
        )}
      </div>

      {error && (
        <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f8d7da', borderRadius: '3px', color: '#721c24' }}>
          <strong>❌ Error:</strong> {error}
        </div>
      )}

      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
        <h4>📋 Available Endpoints:</h4>
        <ul>
          <li><code>GET /api/health</code> - Health check</li>
          <li><code>GET /api/servicenow/test</code> - ServiceNow test</li>
          <li><code>POST /api/servicenow/validate-connection</code> - Validate ServiceNow config</li>
          <li><code>GET /api/servicenow/cis</code> - Get Configuration Items</li>
          <li><code>POST /api/servicenow/ci</code> - Create Configuration Item</li>
        </ul>
        <p><strong>API Base URL:</strong> <code>{API_BASE_URL}</code></p>
      </div>
    </div>
  );
};

export default ApiTestComponent;